import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Breadcrumb, BreadcrumbItem, Progress } from 'reactstrap';
import { t } from 'i18next';
import './surveys.css';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { getSurveyQuestionByQuarter,assesstQuestion } from '../../services/question';
import { toast } from 'react-toastify';

// Define TypeScript types
interface Question {
  id: number;
  text: string;
  type: 'text' | 'multiple-choice' | 'dropdown';
  options?: string[];
  answer?: string;
  question?:any;
}

interface Answers {
  [key: number]: string;
}

interface StepperProps {
  currentStep: number;
  steps: Question[];
}

const Stepper: React.FC<StepperProps> = ({ currentStep, steps }) => {
    const progress = ((currentStep - 1) / steps.length) * 100;
    const { id } = useParams();
    const [questions, setQuestions] = useState<Question[]>([]);

    const getQuestion = async (id: string,quarter:string,year:number) => {
      const response = await getSurveyQuestionByQuarter(id,quarter,year);
      if(response.success){
      setQuestions(response.data);
      }
      else{
        toast.error(response.message);
      }
    };

    useEffect(() => {
      if(id){
        getQuestion(id,'Q1',2024);
      }
    }, [id]);
  
    return (
      <div className="stepper-wrapper mb-4">
        <div className="stepper-progress d-flex align-items-center">
          <div className="flex-grow-1 me-3">
            <Progress value={progress} color="#ef804c" /> {/* Progress bar */}
          </div>
          <div className="stepper-percentage px-2">
            {Math.round(progress)}% Complete {/* Percentage text */}
          </div>
        </div>
      </div>
    );
  };

const ReplayQuestion: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [answers, setAnswers] = useState<Answers>({});
    const [questions, setQuestions] = useState<Question[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [selectedQuarter, setSelectedQuarter] = useState<string>('Q1');
    const[selectedYear,setSelectedYear] = useState<number>(2024);
   
    const fetchQuestions = async (id: string, quarter: string, year: number) => {
      if (year && quarter && quarter.length > 0 && quarter.length !== 0) {
        
        const response = await getSurveyQuestionByQuarter(id, quarter, year);
        if (response?.success) {
            setQuestions(response?.data);
            setLoading(false);

            // Set initial answers if they existquestion_type
            const initialAnswers = response.data.reduce((acc: Answers, question: Question) => {
                if (question.answer) {
                    acc[question.question.id] = question.answer;
                }
                return acc;
            }, {});
           // //console.log(initialAnswers);
            setAnswers(initialAnswers);

        } else {
            toast.error(response.message);
        }
      }
    };

    useEffect(() => {
        if (id) {
            fetchQuestions(id, selectedQuarter,selectedYear);
        }
    }, [id, selectedQuarter,selectedYear]);

    const handleQuarterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedQuarter(e.target.value);
    };

    const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedYear(parseInt(e.target.value));
    };

    const handleAnswerChange = (step: number, value: string) => {
        setAnswers((prev) => ({ ...prev, [step]: value }));
    };

    const handleNext = async () => {
     
      if (selectedYear && selectedQuarter && selectedQuarter.length > 0 && selectedQuarter.length !== 0) {
        setSubmitting(true);
        try {
            // Submit the current answer
            const currentAnswer = { [questions[currentStep - 1].id]: answers[currentStep] };
            const response = await assesstQuestion({surveyId:id,questionId:questions[currentStep - 1]?.question?.id,answer:answers[currentStep],year:selectedYear,quarter:selectedQuarter}); // Implement this API call
            if (response.success) {
                toast.success('Answer saved successfully!');
                if (currentStep < questions.length) {
                    setCurrentStep((prev) => prev + 1);
                } else {
                    alert('All answers submitted successfully!');
                }
            } else {
                toast.error('Failed to save answer.');
            }
        } catch (err) {
            toast.error('Submission failed. Please try again.');
        } finally {
            setSubmitting(false);
        }
      }
    };

    const handlePrevious = () => {
        if (currentStep > 1) {
            setCurrentStep((prev) => prev - 1);
        }
    };

  const renderQuestionInput = (question: any) => {
    if (!question || !question.question_type) {
        return <div className="text-muted">Question type is not defined.</div>;
    }

    switch (question.question_type) {
        case 'textarea':
            return (
                <input
                    type="text"
                    className="form-control"
                    value={answers[question.id] || ''}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                />
            );
        case 'multiple_choice':
            const choicesArray = Array.isArray(question.choices)
                ? question.choices
                : question.choices?.split(',');
            return (
                <div>
                    {choicesArray?.map((option: any) => (
                        <div key={option} className="form-check">
                            <input
                                className="form-check-input"
                                type="radio"
                                name={`question-${question.id}`}
                                value={option}
                                checked={answers[question.id] === option}
                                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                            />
                            <label className="form-check-label px-4">{option}</label>
                        </div>
                    ))}
                </div>
            );
        case 'single_choice':
        case 'compliance_related':
        case 'risk_related':
            const choices = Array.isArray(question.choices)
                ? question.choices
                : question.choices?.split(',');
            return (
                <select
                    className="form-select"
                    value={answers[question.id] || ''}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                >
                    <option value="">Select an option</option>
                    {choices?.map((option: any) => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </select>
            );
        default:
            return <div className="text-muted">Unsupported question type</div>;
    }
};


    if (loading) {
        return (
            <div className="container mt-5 text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="root">
            <Breadcrumb>
                <BreadcrumbItem className="px-1">{t('header.you_are_here')}</BreadcrumbItem>
                <span className="mx-2">/</span>
                <BreadcrumbItem active>{t('question.Replay Question')}</BreadcrumbItem>
            </Breadcrumb>

            <h5 className="mb-4">{t('question.Replay Question')}</h5>
            <div className="container mt-3">
            <div className="card bg-white">
            <div className="card-body">
                <div className="mb-3 d-flex">
                    <div className="me-3">
                        <label className="form-label" style={{color:'#3296d4'}}>Select Year</label>
                        <select className="form-select" value={selectedYear} onChange={handleYearChange}>
                            <option value="">Select a year</option>
                            {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="form-label" style={{color:'#3296d4'}}>Select Quarter</label>
                        <select className="form-select" value={selectedQuarter} onChange={handleQuarterChange} disabled={!selectedYear}>
                            <option value="">Select a quarter</option>
                            <option value="Q1">Q1</option>
                            <option value="Q2">Q2</option>
                            <option value="Q3">Q3</option>
                            <option value="Q4">Q4</option>
                        </select>
                    </div>
                    </div>
                </div>
            </div>
            <br></br>
                <div className="card bg-white">
                    <div className="card-header bg-white p-4">
                        <Stepper currentStep={currentStep} steps={questions} />
                    </div>
                    <div className="card-body">
                        <div className="mb-3">
                            <label className="form-label h5">
                                {(questions[currentStep-1]&&questions[currentStep-1]?.question )&&questions[currentStep-1]?.question?.question_en}
                                
                            </label>
                            {renderQuestionInput(questions[currentStep - 1]?.question)}
                        </div>
                    </div>
                    <div className="card-footer d-flex justify-content-between bg-white">
                        <button
                            className="submit-button2"
                            onClick={handlePrevious}
                            disabled={currentStep === 1}
                        >
                            Previous
                        </button>
                        <button
                            className="submit-button2"
                            onClick={handleNext}
                            disabled={submitting}
                        >
                            {submitting ? 'Submitting...' : currentStep === questions.length ? 'Submit' : 'Next'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReplayQuestion;